import type { AnchorHTMLAttributes, ForwardedRef, MouseEvent, ReactNode } from "react";
import { forwardRef } from "react";
import { Link as RouterLink, useNavigate } from "react-router-dom";

type Href = string | { pathname?: string; query?: Record<string, string | number> } | URL;

type LinkProps = Omit<AnchorHTMLAttributes<HTMLAnchorElement>, "href"> & {
  href: Href;
  replace?: boolean;
  scroll?: boolean;
  prefetch?: boolean | null | "auto";
  shallow?: boolean;
  passHref?: boolean;
  locale?: string | false;
  legacyBehavior?: boolean;
  children?: ReactNode;
};

/**
 * Drop-in replacement for `next/link`.
 *
 * Handles the three shapes the storefront actually passes:
 *   - a string href (`/products/${slug}`)
 *   - an object with a pathname plus a query object
 *   - a URL instance
 *
 * `next/link` disabled prefetching for dynamic routes by default and only ever
 * sent `onClick` handlers through; both behaviours are preserved so existing
 * components such as `product-card.tsx` keep working unmodified.
 */
export const Link = forwardRef(function Link(
  {
    href,
    replace,
    scroll,
    prefetch,
    shallow,
    passHref,
    locale,
    legacyBehavior,
    onClick,
    children,
    ...rest
  }: LinkProps,
  ref: ForwardedRef<HTMLAnchorElement>,
) {
  void prefetch;
  void shallow;
  void passHref;
  void locale;
  void legacyBehavior;

  const navigate = useNavigate();
  const to = resolveHref(href);

  function handleClick(event: MouseEvent<HTMLAnchorElement>) {
    onClick?.(event);
    if (event.defaultPrevented) return;

    // Let the browser handle modified clicks, external links and downloads.
    if (
      event.button !== 0 ||
      event.metaKey ||
      event.ctrlKey ||
      event.shiftKey ||
      event.altKey ||
      rest.target === "_blank"
    ) {
      return;
    }

    event.preventDefault();
    if (replace) navigate(to, { replace: true });
    else navigate(to);

    if (scroll !== false) {
      requestAnimationFrame(() => window.scrollTo({ top: 0, behavior: "smooth" }));
    }
  }

  return (
    <RouterLink to={to} ref={ref} onClick={handleClick} {...rest}>
      {children}
    </RouterLink>
  );
});

export function resolveHref(href: Href): string {
  if (typeof href === "string") return href;
  if (href instanceof URL) return `${href.pathname}${href.search}${href.hash}`;

  const pathname = href.pathname ?? "/";
  const query = href.query
    ? `?${new URLSearchParams(
        Object.entries(href.query).map(([key, value]) => [key, String(value)]),
      ).toString()}`
    : "";

  return `${pathname}${query}`;
}

export default Link;